import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Mail,
  Filter,
  RefreshCw,
  Eye,
  Search,
  Trash2,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Play
} from 'lucide-react';
import { API_BASE_URL } from '../../../config/api';
import { getAnalytics, getVisitsByEmail, getRegistrationAttempts } from '../../../redux/api';
import HeaderSection from '../../header/HeaderSection';

const ANALYTICS_DEFAULT_RANGE_DAYS = 7;
const VISITS_PER_PAGE = 10;
const MARKETING_PER_PAGE = 10;

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const buildDefaultFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - ANALYTICS_DEFAULT_RANGE_DAYS);
  return {
    pageType: 'all',
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
    email: ''
  };
};

const AnalyticsView = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [visitsPage, setVisitsPage] = useState(1);
  const [visitsPagination, setVisitsPagination] = useState(null);
  const [marketingPage, setMarketingPage] = useState(1);
  const [marketingPagination, setMarketingPagination] = useState(null);
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [deleteDates, setDeleteDates] = useState({ startDate: '', endDate: '', email: '' });
  const [deleting, setDeleting] = useState(false);
  const [unfinishedSignups, setUnfinishedSignups] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, [filters, visitsPage, marketingPage]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const params = {
        visitsPage,
        visitsLimit: VISITS_PER_PAGE,
        marketingPage,
        marketingLimit: MARKETING_PER_PAGE
      };

      if (filters.pageType !== 'all') params.pageType = filters.pageType;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.email) params.email = filters.email;

      const response = await getAnalytics(params);

      if (response.data.success) {
        setStats(response.data.stats);
        setRecentVisits(response.data.stats.recentVisits || []);
        setVisitsPagination(response.data.stats.visitsPagination || null);
        setMarketingPagination(response.data.stats.marketingAttribution?.pagination || null);
      } else {
        toast.error('Greška pri učitavanju statistike');
      }
      try {
        const attemptsRes = await getRegistrationAttempts({ unfinished: 1, limit: 50 });
        setUnfinishedSignups(attemptsRes.data?.attempts || []);
      } catch {
        setUnfinishedSignups([]);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Greška pri učitavanju statistike');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (next) => {
    setVisitsPage(1);
    setMarketingPage(1);
    setFilters(next);
  };

  const handleViewDetails = async (visit) => {
    if (visit.email) {
      try {
        const response = await getVisitsByEmail(visit.email);
        if (response.data.success) {
          setSelectedVisit({ ...visit, allVisits: response.data.visits });
          setShowDetails(true);
        }
      } catch (error) {
        toast.error('Greška pri učitavanju detalja');
      }
    } else {
      setSelectedVisit(visit);
      setShowDetails(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.notAvailable');
    return new Date(dateString).toLocaleString('sr-RS', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isMarketingVisit = (visit) => {
    const landing = visit?.landingPage || '';
    return visit?.pageType === 'home_seo' || String(landing).startsWith('marketing:');
  };

  const getPageTypeLabel = (type, landingPage) => {
    if (isMarketingVisit({ pageType: type, landingPage })) return 'Početna (marketing)';
    const labels = {
      home: 'Home (React)',
      home_seo: 'Početna (marketing)',
      login: 'Login',
      register: 'Register',
      admin: 'Admin',
      tutorials: 'Tutorijali',
      other: 'Ostalo'
    };
    return labels[type] || type;
  };

  const formatLandingClick = (click) => {
    const element = click?.element || '';
    const labels = {
      'hero-video-play': 'Pustio hero video',
      'hero-video-ended': 'Odgledao hero video do kraja',
      'hero-video-register': 'Registracija preko hero videa',
      'section_view:hero': 'Sekcija: Hero',
      'section_view:hero-video': 'Sekcija: Hero video',
      'section_view:problemi': 'Sekcija: Problemi',
      'section_view:resenje': 'Sekcija: Rešenje',
      'section_view:ekrani': 'Sekcija: Ekrani',
      'section_view:zasto': 'Sekcija: Zašto',
      'section_view:cene': 'Sekcija: Cenovnik',
      'section_view:iskustva': 'Sekcija: Iskustva',
      'section_view:statistika': 'Sekcija: Statistika',
      'section_view:upotreba': 'Sekcija: Upotreba',
      'section_view:pouzdanost': 'Sekcija: Pouzdanost',
      'section_view:blog': 'Sekcija: Blog na početnoj',
      'section_view:cta': 'Sekcija: Donji CTA',
      'section_view:kontakt': 'Sekcija: Kontakt'
    };
    return labels[element] || element || click?.elementType || 'Klik';
  };

  const formatUtm = (visit) =>
    [visit.utmSource, visit.utmMedium, visit.utmCampaign].filter(Boolean).join(' · ') ||
    (visit.referrer ? 'referrer' : '—');

  const handleDeleteVisits = async () => {
    if (deleteDates.email && (!deleteDates.startDate || !deleteDates.endDate)) {
      if (!window.confirm(`Obrisati SVE logove za ${deleteDates.email}?`)) return;
    } else if (!deleteDates.startDate || !deleteDates.endDate) {
      toast.error('Unesite oba datuma ili email');
      return;
    } else if (new Date(deleteDates.startDate) > new Date(deleteDates.endDate)) {
      toast.error('Datum početka mora biti pre datuma završetka');
      return;
    } else if (
      !window.confirm(
        deleteDates.email
          ? `Obrisati logove ${deleteDates.startDate} — ${deleteDates.endDate} za ${deleteDates.email}?`
          : `Obrisati logove ${deleteDates.startDate} — ${deleteDates.endDate}?`
      )
    ) {
      return;
    }

    try {
      setDeleting(true);
      let token = null;
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}');
        token = profile?.token || null;
      } catch {
        // ignore
      }

      const response = await fetch(`${API_BASE_URL}/api/analytics/visits`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(deleteDates)
      });
      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Obrisano ${data.deletedCount} logova`);
        setDeleteDates({ startDate: '', endDate: '', email: '' });
        setVisitsPage(1);
        setMarketingPage(1);
        loadAnalytics();
      } else {
        toast.error(data.message || 'Greška pri brisanju');
      }
    } catch {
      toast.error('Greška pri brisanju logova');
    } finally {
      setDeleting(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <HeaderSection title="Analitika poseta" onAdd={() => loadAnalytics()} />

      {unfinishedSignups.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Nedovršene registracije</h3>
          <p className="text-xs text-gray-500 mb-4">
            Email je unet na formi, ali registracija nije završena.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Kompanija</th>
                  <th className="py-2">Poslednji unos</th>
                </tr>
              </thead>
              <tbody>
                {unfinishedSignups.map((row) => (
                  <tr key={row._id || row.email} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900">{row.email}</td>
                    <td className="py-2 pr-4 text-gray-600">{row.companyName || '—'}</td>
                    <td className="py-2 text-gray-500">
                      {row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleString('sr-RS') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filteri */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="h-4 w-4 text-green-600" />
              Filteri
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Podrazumevano poslednjih {ANALYTICS_DEFAULT_RANGE_DAYS} dana · stariji logovi se automatski brišu
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setVisitsPage(1);
                setMarketingPage(1);
                setFilters(buildDefaultFilters());
              }}
              className="px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Poslednjih {ANALYTICS_DEFAULT_RANGE_DAYS} dana
            </button>
            <button
              onClick={loadAnalytics}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Osveži
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip stranice</label>
            <select
              value={filters.pageType}
              onChange={(e) => handleFilterChange({ ...filters, pageType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 bg-white"
            >
              <option value="all">Sve stranice</option>
              <option value="marketing">Početna (marketing)</option>
              <option value="login">Login</option>
              <option value="register">Register</option>
              <option value="admin">Admin</option>
              <option value="tutorials">Tutorijali</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Od datuma</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Do datuma</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email adresa</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={filters.email}
                onChange={(e) => handleFilterChange({ ...filters, email: e.target.value })}
                placeholder="Pretraži po emailu..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-gray-900 bg-white"
              />
            </div>
          </div>
        </div>
        {stats?.meta && (
          <p className="text-xs text-gray-500 mt-3">
            Period: {stats.meta.startDate} — {stats.meta.endDate} · zadržavanje u bazi: {stats.meta.retentionDays} dana
          </p>
        )}
      </div>

      {/* UTM / kampanje */}
      {stats?.marketingAttribution && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="h-5 w-5 text-amber-600" />
              Kampanje / UTM posete
            </h3>
            {marketingPagination && (
              <span className="text-sm text-gray-500">
                {marketingPagination.total} ukupno · strana {marketingPagination.page} / {marketingPagination.totalPages}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-4">{stats.marketingAttribution.note}</p>

          {stats.marketingAttribution.byCampaign &&
            Object.keys(stats.marketingAttribution.byCampaign).length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {Object.entries(stats.marketingAttribution.byCampaign).map(([key, count]) => (
                  <span
                    key={key}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-900"
                  >
                    {key}: <strong className="ml-1">{count}</strong>
                  </span>
                ))}
              </div>
            )}

          {stats.marketingAttribution.recentVisits?.length > 0 ? (
            <>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600">Datum</th>
                      <th className="px-3 py-2 text-left text-gray-600">Email</th>
                      <th className="px-3 py-2 text-left text-gray-600">Stranica</th>
                      <th className="px-3 py-2 text-left text-gray-600">UTM / izvor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.marketingAttribution.recentVisits.map((v) => (
                      <tr key={v.id} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{formatDate(v.createdAt)}</td>
                        <td className="px-3 py-2 text-gray-900 font-medium">{v.email || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">
                          {getPageTypeLabel(v.pageType, v.landingPage)}
                          {v.landingPage ? (
                            <span className="block text-gray-400 truncate max-w-[180px]">{v.landingPage}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{formatUtm(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {marketingPagination && marketingPagination.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    disabled={marketingPage <= 1 || loading}
                    onClick={() => setMarketingPage((p) => Math.max(1, p - 1))}
                    className="flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prethodna
                  </button>
                  <span className="text-sm text-gray-600">
                    {marketingPage} / {marketingPagination.totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={marketingPage >= marketingPagination.totalPages || loading}
                    onClick={() => setMarketingPage((p) => p + 1)}
                    className="flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 disabled:opacity-40"
                  >
                    Sledeća
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">Nema UTM poseta u izabranom periodu.</p>
          )}
        </div>
      )}

      {stats?.landingSectionAnalytics && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Play className="h-5 w-5 text-green-600" />
            Hero video i sekcije početne
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Video je ukupno (svi pregledi, ne samo poslednja nedelja). Tabela sekcija ispod je za izabrani period.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500">Video viđen (ukupno)</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.landingSectionAnalytics.heroVideo?.viewed || 0}
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="text-xs text-gray-500">Pustio video (ukupno)</p>
              <p className="text-2xl font-bold text-green-800">
                {stats.landingSectionAnalytics.heroVideo?.played || 0}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500">Odgledao do kraja (ukupno)</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.landingSectionAnalytics.heroVideo?.ended || 0}
              </p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-gray-500">Registracija preko videa (ukupno)</p>
              <p className="text-2xl font-bold text-amber-900">
                {stats.landingSectionAnalytics.heroVideo?.register || 0}
              </p>
            </div>
          </div>
          {stats.landingSectionAnalytics.sections?.length > 0 && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-600">Sekcija</th>
                    <th className="px-3 py-2 text-left text-gray-600">Anchor</th>
                    <th className="px-3 py-2 text-right text-gray-600">Posete</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.landingSectionAnalytics.sections.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-900">{row.label}</td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-xs">#{row.id}</td>
                      <td className="px-3 py-2 text-right font-medium">{row.views || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tutorijali — samo po emailu */}
      {stats?.tutorialAnalytics && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-indigo-600" />
            Tutorijali (po emailu)
          </h3>
          {stats.tutorialAnalytics.note && (
            <p className="text-xs text-gray-500 mb-4">{stats.tutorialAnalytics.note}</p>
          )}
          {stats.tutorialAnalytics.uniqueEmails?.length > 0 ? (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-600">Email</th>
                    <th className="px-4 py-2 text-right text-gray-600">Pregledi</th>
                    <th className="px-4 py-2 text-right text-gray-600">Play</th>
                    <th className="px-4 py-2 text-right text-gray-600">Log /tutorials</th>
                    <th className="px-4 py-2 text-left text-gray-600">Poslednji put</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.tutorialAnalytics.uniqueEmails.map((row) => (
                    <tr key={row.email} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-900 font-medium">{row.email}</td>
                      <td className="px-4 py-2 text-right">{row.pageViews || 0}</td>
                      <td className="px-4 py-2 text-right">{row.plays || 0}</td>
                      <td className="px-4 py-2 text-right">{row.legacyVisits || 0}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">{formatDate(row.lastSeen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Nema tutorijal aktivnosti sa emailom.</p>
          )}
        </div>
      )}

      {/* Posete sa emailom — paginacija */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Posete sa emailom</h3>
          {visitsPagination && (
            <span className="text-sm text-gray-500">
              {visitsPagination.total} ukupno · strana {visitsPagination.page} / {visitsPagination.totalPages}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stranica</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Putanja</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">UTM</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akcije</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentVisits.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Nema poseta sa emailom u periodu
                  </td>
                </tr>
              ) : (
                recentVisits.map((visit) => (
                  <tr key={visit.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{visit.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {getPageTypeLabel(visit.pageType, visit.landingPage)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500 max-w-[140px] truncate">{visit.landingPage || '—'}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">{formatUtm(visit)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(visit.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetails(visit)}
                        className="text-green-600 hover:text-green-900 flex items-center text-sm"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Detalji
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {visitsPagination && visitsPagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <button
              type="button"
              disabled={visitsPage <= 1 || loading}
              onClick={() => setVisitsPage((p) => Math.max(1, p - 1))}
              className="flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Prethodna
            </button>
            <span className="text-sm text-gray-600">
              {visitsPage} / {visitsPagination.totalPages}
            </span>
            <button
              type="button"
              disabled={visitsPage >= visitsPagination.totalPages || loading}
              onClick={() => setVisitsPage((p) => p + 1)}
              className="flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 disabled:opacity-40"
            >
              Sledeća
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}
      </div>

      {/* Detalji modal */}
      {showDetails && selectedVisit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Detalji — {selectedVisit.email}</h3>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedVisit(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {selectedVisit.allVisits?.length > 0 ? (
                <div className="space-y-6">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Stranica</th>
                        <th className="px-3 py-2 text-left">Putanja</th>
                        <th className="px-3 py-2 text-left">Datum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedVisit.allVisits.map((visit) => (
                        <tr key={visit.id} className="border-t">
                          <td className="px-3 py-2">{getPageTypeLabel(visit.pageType, visit.landingPage)}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{visit.landingPage || '—'}</td>
                          <td className="px-3 py-2 text-xs">{formatDate(visit.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {selectedVisit.allVisits.some((visit) => visit.clicks?.length) && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Sekcije i video</h4>
                      <ul className="space-y-1 text-sm">
                        {selectedVisit.allVisits.flatMap((visit) =>
                          (visit.clicks || [])
                            .filter(
                              (click) =>
                                String(click.element || '').startsWith('section_view:') ||
                                String(click.element || '').startsWith('hero-video-')
                            )
                            .map((click, index) => (
                              <li
                                key={`${visit.id}-${index}`}
                                className="flex justify-between gap-3 text-gray-700"
                              >
                                <span>{formatLandingClick(click)}</span>
                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                  {formatDate(click.timestamp)}
                                </span>
                              </li>
                            ))
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Nema dodatnih zapisa.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ručno brisanje — dno */}
      <div className="bg-white rounded-lg shadow border border-red-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-red-600" />
          Ručno brisanje logova
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Automatski se brišu logovi stariji od {stats?.meta?.retentionDays ?? 7} dana.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="email"
            value={deleteDates.email}
            onChange={(e) => setDeleteDates({ ...deleteDates, email: e.target.value })}
            placeholder="Email (opciono)"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 bg-white"
          />
          <input
            type="date"
            value={deleteDates.startDate}
            onChange={(e) => setDeleteDates({ ...deleteDates, startDate: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 bg-white"
          />
          <input
            type="date"
            value={deleteDates.endDate}
            onChange={(e) => setDeleteDates({ ...deleteDates, endDate: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 bg-white"
          />
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleDeleteVisits}
            disabled={deleting || (!deleteDates.startDate && !deleteDates.email) || (!deleteDates.endDate && !deleteDates.email)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
          >
            {deleting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Obriši logove
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsView;
